// Reducer, state types, and the PURE projection for the Market Probabilities
// board, factored out of the provider so the reducer/state and the projection sit
// in one place the provider, the effect hooks, and the tests all import. No React,
// no WS -- every export here is pure and unit-testable.
//
// DATA SOURCE (IM-1954): the board is driven by the backend
// `subscribe.market_probabilities` stream, NOT a client-side generic_pricer
// fan-out. The stream pushes each token's whole board (all expiries x models x
// levels) as `update` messages; the parser (feed.ts) normalises them and the
// reducer stores the ParsedFeed per token (SET_FEED). The LEVELS the board
// renders come from the feed (per expiry), so there is no synthetic SoM-anchored
// grid any more; the projection reads the active (expiry, token) node straight
// off the feed. The MTD candlestick machine (use-mtd-candles) still supplies the
// expiry-independent anchor context -- somSpot / mtdHigh / mtdLow (Open/High/Low
// tiles) and the HIT flag, computed against the feed's own levels.
import {
  monthEndExpiry,
  rungHit,
  type TouchDirection,
} from '../../pages/market-probabilities/ladder';
import { MARKETS } from './markets';
import { nodeLevels } from './feed';
import type { FeedFamily, FeedNode, ParsedFeed } from './feed';

// The per-rung touch quote the CARD consumes. level/direction/hit are the rung
// structure; probBs/probLv/probSv are the feed's per-model touch probabilities at
// this level (bs is the faded reference, lv the headline, sv the stochastic-vol
// column). A HIT rung reads 100% in every model column.
export interface RungQuote {
  level: number;
  direction: TouchDirection;
  probBs: number | null;
  probLv: number | null;
  probSv: number | null;
  loading: boolean;
  error: string | null;
  hit: boolean;
}

// The value a model column DISPLAYS for a rung. `hit` is the EFFECTIVE,
// variant-resolved breach flag the CALLER passes (NOT rung.hit): OTC forward-
// month tabs suppress it, so OTC always renders the backend forward pv AS-IS,
// while PREDICTION marks breached (hi/lo-touched) levels 100%. When hit, the rung
// reads 1 (100%) in every model column -- a historical touch is model-independent.
// Otherwise the model's own pv: LV falls back to BS on a BS-only board so the
// headline column stays populated; BS and SV have no fallback (a missing leg reads
// null -> the card shows an em-dash). Pure -- exported for the cell-display tests.
export function displayTouchProb(
  rung: RungQuote,
  model: 'bs' | 'lv' | 'sv',
  hit: boolean,
): number | null {
  if (hit) return 1;
  if (model === 'bs') return rung.probBs;
  if (model === 'sv') return rung.probSv;
  return rung.probLv ?? rung.probBs;
}

// Expiry-INDEPENDENT per-token anchor state, built from the month-to-date (and
// year-to-date) candlestick series. Renders the banner ccy, the Open/High/Low
// tiles, and feeds the HIT computation. The rung LEVELS are NOT here any more --
// they come from the feed, per expiry.
export interface AnchorState {
  ccy: string | null;
  // Registry-resolved vol-surface base_quote (e.g. 'XRP_USDC' for the 'XRP'
  // market). This — NOT the display token — is what the pricer / market-data
  // endpoints key on, so the click-to-pricer handoff seeds with it. Null until
  // coverage resolves.
  baseQuote: string | null;
  somSpot: number | null;
  mtdHigh: number | null;
  mtdLow: number | null;
  soySpot: number | null;
  ytdHigh: number | null;
  ytdLow: number | null;
  ytdStatus: 'pending' | 'ok' | 'error';
  covered: boolean;
  mtdStatus: 'pending' | 'ok' | 'error';
}

// Board-level liveness of the `subscribe.market_probabilities` stream ON THIS
// ENVIRONMENT (IM-19xx). Distinct from per-token/per-expiry ladder state:
//   'connecting'  -- subscribe in flight / pre-timeout; the feed may yet exist,
//                    so the board shows its normal warming affordance.
//   'live'        -- at least one board push has arrived; render normally.
//   'unavailable' -- the subscribe errored (JSON-RPC error) OR no push landed
//                    within the warm-up timeout -> the board shows a calm
//                    full-board empty-state instead of spinning forever. This is
//                    the pre-backend staging case (method returns -32601).
// Invariant: a push always wins. 'connecting'/'unavailable' -> 'live' on the
// first push (so a feed that comes online recovers automatically), but 'live'
// never downgrades to 'unavailable' -- a working board is not blanked by a late
// timeout.
export type FeedStatus = 'connecting' | 'live' | 'unavailable';

export interface MarketProbabilitiesState {
  expiry: string;
  markets: Record<string, AnchorState>;
  // The parsed feed per token (whole board: both families, all expiries, all
  // models). Keyed by the PAYLOAD token, so a push can only ever land in its own
  // token's slot -- the routing-isolation guarantee.
  feedByToken: Record<string, ParsedFeed>;
  // Board-level stream liveness (see FeedStatus). Starts 'connecting'.
  feedStatus: FeedStatus;
}

export type MarketProbabilitiesAction =
  | { type: 'SET_EXPIRY'; expiry: string }
  | { type: 'SET_MTD'; token: string; somSpot: number; mtdHigh: number; mtdLow: number }
  | { type: 'MTD_ERROR'; token: string }
  | { type: 'SET_YTD'; token: string; soySpot: number; ytdHigh: number; ytdLow: number }
  | { type: 'YTD_ERROR'; token: string }
  | { type: 'SET_FEED'; feed: ParsedFeed }
  // The stream could not be established on this environment. `reason` is
  // diagnostic only ('error' = JSON-RPC error reply; 'timeout' = no push within
  // the warm-up window); both resolve to feedStatus 'unavailable'.
  | { type: 'FEED_UNAVAILABLE'; reason: 'error' | 'timeout' }
  | {
      type: 'SET_COVERAGE';
      covered: Record<string, boolean>;
      ccy: Record<string, string | null>;
      // Optional so existing dispatchers/tests that omit it keep working; when
      // present it flows the registry-resolved base_quote onto each market.
      baseQuote?: Record<string, string | null>;
    };

export function reducer(
  state: MarketProbabilitiesState,
  action: MarketProbabilitiesAction,
): MarketProbabilitiesState {
  switch (action.type) {
    case 'SET_EXPIRY': {
      // Pure pointer move: the active tab now reads a different (family, expiry)
      // slice of the feed. Nothing to clear -- the feed store is per token and
      // the projection reads the active expiry's node. No-op if unchanged.
      if (action.expiry === state.expiry) return state;
      return { ...state, expiry: action.expiry };
    }
    case 'SET_COVERAGE': {
      const markets = { ...state.markets };
      for (const [token, covered] of Object.entries(action.covered)) {
        const m = markets[token];
        if (!m) continue;
        const ccy = action.ccy[token] ?? m.ccy;
        const baseQuote = action.baseQuote?.[token] ?? m.baseQuote;
        if (m.covered !== covered || m.ccy !== ccy || m.baseQuote !== baseQuote)
          markets[token] = { ...m, covered, ccy, baseQuote };
      }
      return { ...state, markets };
    }
    case 'SET_MTD': {
      const m = state.markets[action.token];
      if (!m) return state;
      return {
        ...state,
        markets: {
          ...state.markets,
          [action.token]: {
            ...m,
            somSpot: action.somSpot,
            mtdHigh: action.mtdHigh,
            mtdLow: action.mtdLow,
            mtdStatus: 'ok',
          },
        },
      };
    }
    case 'MTD_ERROR': {
      const m = state.markets[action.token];
      if (!m || m.mtdStatus === 'ok') return state;
      return {
        ...state,
        markets: { ...state.markets, [action.token]: { ...m, mtdStatus: 'error' } },
      };
    }
    case 'SET_YTD': {
      const m = state.markets[action.token];
      if (!m) return state;
      return {
        ...state,
        markets: {
          ...state.markets,
          [action.token]: {
            ...m,
            soySpot: action.soySpot,
            ytdHigh: action.ytdHigh,
            ytdLow: action.ytdLow,
            ytdStatus: 'ok',
          },
        },
      };
    }
    case 'YTD_ERROR': {
      const m = state.markets[action.token];
      if (!m || m.ytdStatus === 'ok') return state;
      return {
        ...state,
        markets: { ...state.markets, [action.token]: { ...m, ytdStatus: 'error' } },
      };
    }
    case 'SET_FEED': {
      // Store the whole parsed board under its payload token. Latest-wins per
      // token (each push carries the full board), so a re-publish simply
      // replaces the prior snapshot for that token. A push is proof the stream is
      // live: flip feedStatus 'live' (recovering from a prior 'connecting' OR
      // 'unavailable' -- a feed that comes online repopulates the board).
      return {
        ...state,
        feedByToken: { ...state.feedByToken, [action.feed.token]: action.feed },
        feedStatus: 'live',
      };
    }
    case 'FEED_UNAVAILABLE': {
      // Only degrade a board that has not yet proved itself. Once 'live' a push
      // has landed, so a late timeout / stray error must NOT blank a working
      // board -- no-op. From 'connecting' -> 'unavailable' (calm empty-state).
      if (state.feedStatus !== 'connecting') return state;
      return { ...state, feedStatus: 'unavailable' };
    }
    default:
      return state;
  }
}

// Merge a feed node's levels + the MTD extremes into the RungQuote[] the card
// consumes. HIT is computed against the feed's own levels (an upper level the MTD
// high has reached, or a lower level the MTD low has reached). Levels arrive from
// the parser sorted descending; the card splits upper/lower on the spot marker.
// Pure -- exported for the projection + reducer tests.
export function projectFeedNode(
  node: FeedNode | undefined,
  mtdHigh: number | null,
  mtdLow: number | null,
  onTouch = true,
  inQuote = true,
): RungQuote[] {
  if (!node) return [];
  return nodeLevels(node, onTouch, inQuote).map((l) => ({
    level: l.level,
    direction: l.direction,
    probBs: l.pvBs,
    probLv: l.pvLv,
    probSv: l.pvSv,
    loading: false,
    error: l.error,
    hit: rungHit({ level: l.level, direction: l.direction }, mtdHigh, mtdLow),
  }));
}

// The sorted, de-duplicated union of a family's expiry keys across every token's
// feed. Drives the OTC (exchange-family) tab strip dynamically off the payload --
// so it lists every expiry the feed serves (~13 today, more when the full Deribit
// ladder ships) rather than a hard-coded set. Keys are "YYYY-MM-DD HH:MM:SS", so a
// lexicographic sort is chronological. Pure -- exported for the unit test.
export function collectFamilyExpiries(
  feedByToken: Record<string, ParsedFeed>,
  family: FeedFamily,
): string[] {
  const seen = new Set<string>();
  for (const feed of Object.values(feedByToken)) {
    for (const key of Object.keys(feed.families[family])) seen.add(key);
  }
  return [...seen].sort();
}

// The ladder region's data state for one (token, active expiry):
//   'loading'  -- no feed has arrived for this token yet (stream still warming).
//   'unlisted' -- the token HAS a feed but does not list the selected expiry
//                 (the OTC tab strip is the union of all tokens' expiries, so a
//                 shorter-listed token legitimately misses some) -> the card shows
//                 a "No listed expiry" empty-state instead of a blank ladder.
//   'listed'   -- the selected-expiry node exists; render its rungs.
export type LadderState = 'loading' | 'unlisted' | 'listed';
export function ladderStateFor(hasFeed: boolean, hasNode: boolean): LadderState {
  if (!hasFeed) return 'loading';
  return hasNode ? 'listed' : 'unlisted';
}

const initialMarketState: AnchorState = {
  ccy: null,
  baseQuote: null,
  somSpot: null,
  mtdHigh: null,
  mtdLow: null,
  soySpot: null,
  ytdHigh: null,
  ytdLow: null,
  ytdStatus: 'pending',
  // Optimistically covered so the card paints its shell on first render; the
  // coverage effect flips registry markets with no surface to uncovered.
  covered: true,
  mtdStatus: 'pending',
};

export const initialState: MarketProbabilitiesState = {
  expiry: monthEndExpiry(),
  markets: Object.fromEntries(
    MARKETS.map((m) => [m.token, { ...initialMarketState, ccy: m.ccy ?? null }]),
  ),
  feedByToken: {},
  feedStatus: 'connecting',
};
